/**
 * ActivityPulse — calm sparkline of today's audit_logs.
 *
 * Per the iter32 backend additions, the Director Overview gets a 4-series
 * sparkline (sign-ins / status updates / messages / pickups) showing today's
 * hourly rhythm + a natural-language summary line.
 *
 * Strictly read-only. Empty/quiet state when nothing happened yet.
 */
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Activity } from "lucide-react";

const SERIES_META = [
  { key: "statuses", label: "Status updates", color: "#1F3A2A" },
  { key: "signins", label: "Sign-ins", color: "#1F8A3F" },
  { key: "messages", label: "Messages", color: "#BE185D" },
  { key: "pickups", label: "Pickups", color: "#B45309" },
];

export default function ActivityPulse({ workspaceId, testid = "activity-pulse" }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!workspaceId) return;
    let alive = true;
    api
      .get(`/admin/workspaces/${workspaceId}/activity-pulse`)
      .then((r) => {
        if (alive) setData(r.data);
      })
      .catch(() => alive && setData({ error: true }));
    return () => {
      alive = false;
    };
  }, [workspaceId]);

  if (!data) {
    return (
      <section
        data-testid={`${testid}-loading`}
        className="card rounded-3xl bg-white border hairline p-6"
      >
        <div className="alouvya-skeleton h-3 w-32" />
        <div className="alouvya-skeleton h-3 w-52 mt-4" />
      </section>
    );
  }
  if (data.error) {
    return null;
  }

  const peak = Math.max(
    1,
    ...SERIES_META.flatMap((m) => data.series[m.key] || [0])
  );

  return (
    <section
      data-testid={testid}
      className="rounded-3xl bg-white border hairline p-6 shadow-[0_2px_8px_rgba(16,24,40,0.04)]"
    >
      <div className="flex items-center gap-2">
        <Activity size={14} strokeWidth={1.6} className="text-[#1F3A2A]" />
        <span className="eyebrow">Activity pulse</span>
      </div>

      <p
        data-testid={`${testid}-summary`}
        className="mt-4 font-display text-lg leading-snug text-[#1A1B2E]"
      >
        {data.summary}
      </p>

      <div className="mt-6 space-y-4">
        {SERIES_META.map((m) => (
          <Spark
            key={m.key}
            label={m.label}
            color={m.color}
            series={data.series[m.key] || []}
            peak={peak}
            testid={`${testid}-spark-${m.key}`}
          />
        ))}
      </div>

      <div className="mt-5 flex items-center justify-between text-[10px] text-[#9CA3AF] tabular-nums">
        <span>00:00</span>
        <span>12:00</span>
        <span>23:59</span>
      </div>
    </section>
  );
}

function Spark({ label, color, series, peak, testid }) {
  // SVG sparkline — 240×34 area, 24 buckets evenly distributed.
  const w = 240;
  const h = 34;
  const padX = 4;
  const innerW = w - padX * 2;
  const total = series.reduce((a, b) => a + b, 0);
  // Build the polyline points.
  const points = series
    .map((v, i) => {
      const x = padX + (i * innerW) / Math.max(1, series.length - 1);
      const y = h - 2 - (v / peak) * (h - 6);
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
  // Area fill polygon.
  const area =
    `${padX},${h - 1} ` + points + ` ${padX + innerW},${h - 1}`;
  return (
    <div data-testid={testid} className="flex items-center gap-4">
      <div className="w-32 shrink-0">
        <div className="text-xs text-[#6B7280] font-medium">{label}</div>
        <div className="tabular-nums text-sm font-semibold text-[#1A1B2E]">
          {total}
        </div>
      </div>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        width="100%"
        height="34"
        preserveAspectRatio="none"
        aria-hidden
        className="flex-1"
      >
        <polygon points={area} fill={color} opacity="0.12" />
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}
